/** The calls of lists (spec-63 E5) under `/api/v1/crm`; the overview itself goes through `query/api`. */
import { crmRequest } from '../http'
import { searchRecords } from '../query/api'
import type { QueryBody } from '../query/types'
import type { ListType } from '../types'
import type { ListChoice, ListDetail, ListInput, ListRow, MembersAdded } from './types'

const send = (method: string, body?: unknown): RequestInit => ({ method, ...(body === undefined ? {} : { body: JSON.stringify(body) }) })

/** What a picker needs of a list; `GET /lists` does not say which ones are archived. */
interface PlainList {
  id: string
  name: string
  type: ListType
}

const ARCHIVED_ONLY: QueryBody = {
  version: 2,
  quick: [{ property: 'archived', operator: 'is_true' }],
  groups: [],
}

/**
 * Every live list, dynamic and archived ones too, for the pickers. The archived ones come from a
 * search on "Gearchiveerd" (at most 50 per page; a picker marks, it does not page).
 */
export async function listChoices(): Promise<ListChoice[]> {
  const [lists, archived] = await Promise.all([
    crmRequest<PlainList[]>('/lists'),
    searchRecords<ListRow>('lijsten', { query: ARCHIVED_ONLY, page_size: 50 }),
  ])
  const gone = new Set(archived.items.map((row) => row.id))
  return lists.map((list) => ({ id: list.id, name: list.name, type: list.type, archived: gone.has(list.id) }))
}

/** The tiles "Komende events en mailings". */
export function upcomingLists(days = 60, limit = 4): Promise<ListRow[]> {
  return crmRequest(`/lists/upcoming?days=${days}&limit=${limit}`)
}

export function getList(listId: string): Promise<ListDetail> {
  return crmRequest(`/lists/${listId}`)
}

/** The dynamic filter goes along as `filter` (filter model v2); the server writes the sentinel itself. */
export function createListV2(input: ListInput): Promise<ListDetail> {
  return crmRequest('/lists', send('POST', input))
}

export function updateListV2(listId: string, patch: ListInput): Promise<ListDetail> {
  return crmRequest(`/lists/${listId}`, send('PATCH', patch))
}

/** The list a legacy event was folded into (404 when the event is gone). */
export function listByEvent(eventId: string): Promise<{ list_id: string }> {
  return crmRequest(`/lists/by-event/${eventId}`)
}

type MembersSelection =
  | { person_ids: string[] }
  | { query: QueryBody; expected_total: number }
  | { organisation_id: string }

export function addListMembers(listId: string, selection: MembersSelection): Promise<MembersAdded> {
  return crmRequest(`/lists/${listId}/members`, send('POST', selection))
}

export function removeListMembers(listId: string, personIds: string[]): Promise<{ removed: number }> {
  return crmRequest(`/lists/${listId}/members/remove`, send('POST', { person_ids: personIds }))
}

// ---- Lifecycle: the actions of the list page ----

/** A copy of the list (members included for a static one); the answer is the new list. */
export function duplicateList(listId: string): Promise<ListDetail> {
  return crmRequest(`/lists/${listId}/duplicate`, send('POST'))
}

/** A dynamic list becomes static with the members it has now. */
export function listToStatic(listId: string): Promise<ListDetail> {
  return crmRequest(`/lists/${listId}/to-static`, send('POST'))
}

export function archiveList(listId: string): Promise<ListDetail> {
  return crmRequest(`/lists/${listId}/archive`, send('POST'))
}

export function unarchiveList(listId: string): Promise<ListDetail> {
  return crmRequest(`/lists/${listId}/unarchive`, send('POST'))
}

export function deleteListV2(listId: string): Promise<void> {
  return crmRequest(`/lists/${listId}`, send('DELETE'))
}
